import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CarCard from "../components/CarCard";
import MobileContactBar from "../components/MobileContactBar";
import useReveal from "../hooks/useReveal";
import { fetchCars } from "../api";
import { CONTACTS } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const priceNum = (p) => (p ? parseInt(p.replace(/[^0-9]/g, ""), 10) || 0 : 0);

export default function Catalog() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [brand, setBrand] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [maxPrice, setMaxPrice] = useState("all");
  const [sort, setSort] = useState("recent");
  useReveal(cars.length);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCars()
      .then((d) => setCars(d.cars || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const brands = useMemo(
    () => Array.from(new Set(cars.map((c) => (c.brand || "").split(" ")[0]))).filter(Boolean).sort(),
    [cars]
  );
  const fuels = useMemo(
    () => Array.from(new Set(cars.map((c) => c.fuel).filter(Boolean))),
    [cars]
  );

  const filtered = useMemo(() => {
    let list = cars.filter((c) => {
      if (brand !== "all" && !(c.brand || "").toLowerCase().startsWith(brand.toLowerCase())) return false;
      if (fuel !== "all" && c.fuel !== fuel) return false;
      if (maxPrice !== "all" && priceNum(c.price) > parseInt(maxPrice, 10)) return false;
      return true;
    });
    if (sort === "asc") list = [...list].sort((a, b) => priceNum(a.price) - priceNum(b.price));
    if (sort === "desc") list = [...list].sort((a, b) => priceNum(b.price) - priceNum(a.price));
    return list;
  }, [cars, brand, fuel, maxPrice, sort]);

  return (
    <>
      <Header />
      <main className="pt-[110px] lg:pt-[130px]">
        <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
          <p className="label-mono mb-5">Auto disponibili</p>
          <h1 className="h-display text-[clamp(2.4rem,5vw,4rem)]">
            Prima l'auto.
            <br />
            Poi tutto quello che sappiamo su di lei.
          </h1>
          <p className="mt-6 max-w-[520px] text-[1.02rem] leading-relaxed text-[#5b5b54]">
            Ogni vettura è stata da noi selezionata dopo un'attenta ispezione
            svolta in loco: qui puoi consultare dati e dettagli compilati di
            persona.
          </p>

          {/* Filter bar */}
          <div className="mt-10 flex flex-col gap-5 border-y border-[var(--cm-line)] py-5">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger className="h-11 w-[190px] rounded-full border-[var(--cm-line)] bg-transparent">
                  <SelectValue placeholder="Tutte le marche" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le marche</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={maxPrice} onValueChange={setMaxPrice}>
                <SelectTrigger className="h-11 w-[190px] rounded-full border-[var(--cm-line)] bg-transparent">
                  <SelectValue placeholder="Qualsiasi prezzo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualsiasi prezzo</SelectItem>
                  <SelectItem value="7000">Fino a 7.000 €</SelectItem>
                  <SelectItem value="12000">Fino a 12.000 €</SelectItem>
                  <SelectItem value="18000">Fino a 18.000 €</SelectItem>
                  <SelectItem value="25000">Fino a 25.000 €</SelectItem>
                  <SelectItem value="40000">Fino a 40.000 €</SelectItem>
                </SelectContent>
              </Select>

              <div className="ml-auto flex items-center gap-4">
                <span className="font-mono-cm text-[0.7rem] uppercase tracking-[0.16em] text-[#8a8a82]">
                  {filtered.length} vetture
                </span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="h-11 w-[180px] rounded-full border-[var(--cm-line)] bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Più recenti</SelectItem>
                    <SelectItem value="asc">Prezzo crescente</SelectItem>
                    <SelectItem value="desc">Prezzo decrescente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {["all", ...fuels].map((f) => (
                <button
                  key={f}
                  onClick={() => setFuel(f)}
                  className={`rounded-full px-4 py-2 text-[0.82rem] transition-colors duration-200 ${
                    fuel === f
                      ? "bg-[var(--cm-ink)] text-white"
                      : "border border-[var(--cm-line)] text-[#5b5b54] hover:border-[var(--cm-ink)]"
                  }`}
                >
                  {f === "all" ? "Tutte" : f}
                </button>
              ))}
            </div>
          </div>

          {/* Grid / states */}
          {error ? (
            <div className="mb-24 mt-12 rounded-[16px] border border-[var(--cm-line)] bg-white p-10 text-center">
              <p className="text-[#4a4a45]">
                Al momento non riusciamo a caricare il catalogo.
              </p>
              <p className="mt-2 text-[0.9rem] text-[#8a8a82]">
                Riprova tra poco oppure chiamaci allo {CONTACTS.ufficio}.
              </p>
            </div>
          ) : loading ? (
            <div className="mb-24 mt-12 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className="animate-pulse">
                  <div className="aspect-[4/3] rounded-[18px] bg-black/5" />
                  <div className="mt-4 h-4 w-2/3 rounded bg-black/5" />
                  <div className="mt-2 h-3 w-1/2 rounded bg-black/5" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="mb-24 mt-12 rounded-[16px] border border-[var(--cm-line)] bg-white p-10 text-center">
              <p className="text-[#4a4a45]">Nessuna auto trovata con questi filtri.</p>
              <button
                onClick={() => { setBrand("all"); setFuel("all"); setMaxPrice("all"); }}
                className="mt-4 rounded-full border border-[var(--cm-line)] px-5 py-2.5 text-[0.88rem] font-medium transition-colors hover:border-[var(--cm-ink)]"
              >
                Azzera i filtri
              </button>
            </div>
          ) : (
            <div className="mb-24 mt-12 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((car) => <CarCard key={car.id} car={car} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <MobileContactBar waText="Ciao Carmaps, vi scrivo dal sito." />
    </>
  );
}
